import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Result, Space, Spin, Table, Tabs, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, InfoCircleOutlined, ReloadOutlined, StopOutlined, TeamOutlined } from '@ant-design/icons';
import { leerFichaEmpleado } from '../../api/empleadosApi';
import type { EmpleadoFicha, EmpleadosCatalogos, HistorialDelegacion, HistorialZona } from '../../api/empleadosApi';
import { interpretarError } from '../../api/altasCasosApi';
import { FotoEmpleado } from './FotoEmpleado';
import { EmpleadoFormModal } from './EmpleadoFormModal';
import { RolesModal } from './RolesModal';
import { POR_CONFIRMAR, ayuda } from './ayudas';
import type { ClaveAyuda } from './ayudas';
import { etiquetaInterlocucion, nombreRol } from './reglas';
import { BajaModal } from './BajaModal';
import { Visibilidad } from './Visibilidad';
import { FctIcono, fmtFecha } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';

const { Text } = Typography;

const vacio = <span style={{ opacity: 0.5 }} aria-label="Sin dato">—</span>;
const texto = (v?: string | null) => (v ? v : vacio);
const numero = (v?: number | null) => (v != null ? <span className="fct-mono">{v}</span> : vacio);
const fecha = (v?: string | null) => (v ? <span className="fct-mono">{fmtFecha(v)}</span> : vacio);
const sino = (v: boolean) => (v ? 'Sí' : 'No');
const nivelInterlocucion = (v?: number | null) => {
  const palabra = etiquetaInterlocucion(v);
  return palabra ? <span>{palabra}</span> : vacio;
};

/** La ayuda sin el «(por confirmar)»: esa marca es del formulario, no de la ficha. */
const textoAyuda = (clave: ClaveAyuda) => ayuda(clave).replace(` ${POR_CONFIRMAR}`, '');

/** Estado como etiqueta rectangular (esquinas de 3 px), siempre con icono y palabra. */
const EtiquetaEstado: React.FC<{ activo: boolean }> = ({ activo }) => {
  const familia = activo ? 'exito' : 'neutro';
  return (
    <span
      data-estado={activo ? 'activo' : 'baja'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '1px 8px', borderRadius: 3, border: '1px solid',
        font: '500 12px/18px var(--fct-fuente-interfaz)',
        background: `var(--fct-${familia}-relleno)`, borderColor: `var(--fct-${familia}-linea)`, color: `var(--fct-${familia}-texto)`,
      }}
    >
      <FctIcono nombre={activo ? 'completado' : 'cancelado'} style={{ width: 13, height: 13 }} />
      {activo ? 'Activo' : 'Baja'}
    </span>
  );
};

const estiloSeccion: React.CSSProperties = { margin: '0 0 16px' };
const estiloTituloSeccion: React.CSSProperties = {
  margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--fct-marca-verde-oscuro)',
};
const estiloRejilla: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', columnGap: 24, margin: 0 };
const estiloFila: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr)', alignItems: 'baseline', gap: 8,
  padding: '4px 0', borderBottom: '1px solid var(--fct-linea-tarjeta)',
};
const estiloEtiqueta: React.CSSProperties = { margin: 0, color: fct.tinta['700'] };

/** Una sección con título corto y su rejilla de filas. */
const Seccion: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <section aria-label={titulo} style={estiloSeccion}>
    <h3 style={estiloTituloSeccion}>{titulo}</h3>
    <dl style={estiloRejilla}>{children}</dl>
  </section>
);

/** Fila etiqueta a la izquierda (ancho fijo) y valor a la derecha; con `ayudaDe`, el icono de ayuda con tooltip. */
const Fila: React.FC<{ etiqueta: string; ayudaDe?: ClaveAyuda; ancha?: boolean; children: React.ReactNode }> = ({ etiqueta, ayudaDe, ancha, children }) => (
  <div className="ficha-fila" style={ancha ? { ...estiloFila, gridColumn: '1 / -1' } : estiloFila}>
    <dt style={estiloEtiqueta}>{etiqueta}</dt>
    <dd style={{ margin: 0, minWidth: 0 }}>
      {children}
      {ayudaDe && (
        <Tooltip title={textoAyuda(ayudaDe)}>
          <InfoCircleOutlined
            tabIndex={0} role="img" aria-label={`Ayuda: ${etiqueta}`} data-ayuda={ayudaDe}
            style={{ marginLeft: 8, fontSize: 13, color: fct.tinta['700'], cursor: 'help' }}
          />
        </Tooltip>
      )}
    </dd>
  </div>
);

const columnaDesde = { title: 'Desde', dataIndex: 'desde', key: 'desde', width: 120, render: (v?: string | null) => fecha(v) };
const columnaHasta = { title: 'Hasta', dataIndex: 'hasta', key: 'hasta', width: 120, render: (v?: string | null) => (v ? fecha(v) : <Text type="secondary">Vigente</Text>) };

const COLUMNAS_DELEGACION: ColumnsType<HistorialDelegacion> = [
  { title: 'Delegación', key: 'delegacion', render: (_, r) => r.delegacion || `Delegación ${r.idDelegacion}` },
  columnaDesde,
  columnaHasta,
];
const COLUMNAS_ZONA: ColumnsType<HistorialZona> = [
  { title: 'Zona', key: 'zona', render: (_, r) => r.zona || `Zona ${r.idZona}` },
  columnaDesde,
  columnaHasta,
];

interface FichaDrawerProps {
  /** Empleado abierto, o `null` con el cajón cerrado. */
  idEmpleado: number | null;
  /** Nombre del listado, para el título mientras llega la ficha. */
  nombre?: string;
  /** El usuario tiene Empleados.Gestionar: solo entonces salen Editar, Roles y Dar de baja. */
  gestionar: boolean;
  /** Catálogos de la pantalla (delegaciones, zonas, roles); `null` mientras llegan. */
  catalogos: EmpleadosCatalogos | null;
  /** Hubo cambios en Gescom: refrescar el listado. */
  onCambiado: () => void;
  onCerrar: () => void;
}

/** El rol por su nombre del catálogo; con el catálogo sin cargar, su código tal cual. */
const etiquetaRol = (guid: string, catalogos: EmpleadosCatalogos | null) =>
  catalogos
    ? nombreRol(guid, catalogos.roles?.find((r) => r.guidRol === guid)?.nombre)
    : <span className="fct-mono">{guid}</span>;

/** Fila vigente (sin `hasta`); si no hay ninguna, la más reciente (vienen por fecha descendente). */
const vigente = <T extends { hasta?: string | null }>(filas: T[]): T | undefined =>
  filas.find((f) => !f.hasta) ?? filas[0];

/** Fila vigente resaltada en los históricos. */
const filaVigente = (r: { hasta?: string | null }): React.HTMLAttributes<HTMLElement> =>
  (r.hasta ? {} : { style: { background: 'var(--fct-interaccion-hover-fila)', fontWeight: 600 }, 'aria-current': 'true' });

interface DatosFichaProps {
  ficha: EmpleadoFicha;
  catalogos: EmpleadosCatalogos | null;
  delegacion?: HistorialDelegacion;
  zona?: HistorialZona;
}

/** Datos de la ficha en secciones: Contacto, Organización, Fechas y niveles, Accesos, y los históricos. */
const DatosFicha: React.FC<DatosFichaProps> = ({ ficha, catalogos, delegacion, zona }) => (
  <>
    <div style={{ marginBottom: 12 }}><EtiquetaEstado activo={ficha.activo} /></div>

    <Seccion titulo="Contacto">
      <Fila etiqueta="Email">{texto(ficha.emailEmpresarial)}</Fila>
      <Fila etiqueta="Teléfono">{ficha.telefono ? <span className="fct-mono">{ficha.telefono}</span> : vacio}</Fila>
    </Seccion>

    <Seccion titulo="Organización">
      <Fila etiqueta="Código corto"><span className="fct-mono">{ficha.codigoCorto || '—'}</span></Fila>
      <Fila etiqueta="Nombre abreviado">{texto(ficha.nombreAbreviado)}</Fila>
      <Fila etiqueta="Puesto">{texto(ficha.puesto)}</Fila>
      <Fila etiqueta="Departamento">{texto(ficha.departamento)}</Fila>
      <Fila etiqueta="Delegación">{delegacion?.delegacion || (ficha.idDelegacion != null ? `Delegación ${ficha.idDelegacion}` : vacio)}</Fila>
      <Fila etiqueta="Zona">{ficha.idZona == null ? vacio : (zona?.zona || `Zona ${ficha.idZona}`)}</Fila>
    </Seccion>

    <Seccion titulo="Fechas y niveles">
      <Fila etiqueta="Fecha de alta">{fecha(ficha.fechaAlta)}</Fila>
      <Fila etiqueta="Fecha de baja">{fecha(ficha.fechaBaja)}</Fila>
      <Fila etiqueta="Nivel de seguridad">{numero(ficha.nivelSeguridad)}</Fila>
      <Fila etiqueta="Nivel de asesor">{numero(ficha.nivelAsesor)}</Fila>
      <Fila etiqueta="Nivel de interlocución">{nivelInterlocucion(ficha.nivelInterlocucion)}</Fila>
    </Seccion>

    <Seccion titulo="Accesos">
      <Fila etiqueta="Acceso a GDGO" ayudaDe="accesoGdgo">{sino(ficha.accesoGdgo)}</Fila>
      <Fila etiqueta="Asignar CCL" ayudaDe="asignarCcl">{sino(ficha.asignarCcl)}</Fila>
      <Fila etiqueta="Control de acompañamiento" ayudaDe="controlAcompaniamiento">{sino(ficha.controlAcompaniamiento)}</Fila>
      <Fila etiqueta="Roles" ancha>
        {ficha.roles.length ? ficha.roles.map((r) => <div key={r}>{etiquetaRol(r, catalogos)}</div>) : vacio}
      </Fila>
    </Seccion>

    <section aria-label="Histórico" style={estiloSeccion}>
      <h3 style={estiloTituloSeccion}>Histórico de delegación</h3>
      <Table<HistorialDelegacion>
        size="small"
        rowKey={(r) => `${r.idDelegacion}-${r.desde ?? ''}-${r.hasta ?? ''}`}
        columns={COLUMNAS_DELEGACION}
        dataSource={ficha.historialDelegaciones}
        pagination={false}
        onRow={filaVigente}
        locale={{ emptyText: 'Sin histórico de delegación' }}
      />
      <h3 style={{ ...estiloTituloSeccion, marginTop: 16 }}>Histórico de zona</h3>
      <Table<HistorialZona>
        size="small"
        rowKey={(r) => `${r.idZona}-${r.desde ?? ''}-${r.hasta ?? ''}`}
        columns={COLUMNAS_ZONA}
        dataSource={ficha.historialZonas}
        pagination={false}
        onRow={filaVigente}
        locale={{ emptyText: 'Sin histórico de zona' }}
      />
    </section>
  </>
);

/** Con Empleados.Gestionar, los datos y la visibilidad van en pestañas; sin él, solo los datos. */
const FichaPestanas: React.FC<{ gestionar: boolean; ficha: EmpleadoFicha; datos: React.ReactNode }> = ({ gestionar, ficha, datos }) => (
  gestionar ? (
    <Tabs
      defaultActiveKey="datos"
      items={[
        { key: 'datos', label: 'Datos', children: datos },
        { key: 'visibilidad', label: 'Visibilidad', children: <Visibilidad idEmpleado={ficha.idEmpleado} nombre={ficha.nombreCompleto} activo={ficha.activo} /> },
      ]}
    />
  ) : <>{datos}</>
);

/** Ficha de un empleado: datos profesionales (§2) e históricos de delegación y zona. */
export const FichaDrawer: React.FC<FichaDrawerProps> = ({ idEmpleado, nombre, gestionar, catalogos, onCambiado, onCerrar }) => {
  const [ficha, setFicha] = useState<EmpleadoFicha | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<'editar' | 'roles' | 'baja' | null>(null);
  const [recarga, setRecarga] = useState(0);
  // Solo vale la última lectura: una respuesta tardía de otro empleado no puede pintarse bajo este título.
  const ultima = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++ultima.current;
    setFicha(null);
    setError(null);
    if (idEmpleado == null) return;
    setCargando(true);
    try {
      const f = await leerFichaEmpleado(idEmpleado);
      if (n === ultima.current) setFicha(f);
    } catch (e) {
      if (n === ultima.current) setError(interpretarError(e).mensaje);
    } finally {
      if (n === ultima.current) setCargando(false);
    }
  }, [idEmpleado]);

  useEffect(() => { cargar(); }, [cargar, recarga]);

  const trasCambio = () => { setRecarga((n) => n + 1); onCambiado(); };

  const delegacion = ficha ? vigente(ficha.historialDelegaciones) : undefined;
  const zona = ficha ? vigente(ficha.historialZonas) : undefined;

  return (
    <Drawer
      open={idEmpleado != null}
      onClose={onCerrar}
      width={760}
      title={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <FotoEmpleado nombre={ficha?.nombreCompleto ?? nombre ?? ''} email={ficha?.emailEmpresarial} tamano={40} />
          <span>{ficha?.nombreCompleto ?? nombre ?? 'Empleado'}</span>
        </span>
      )}
      destroyOnClose
      extra={gestionar && ficha && (
        <Space>
          <Button icon={<EditOutlined />} disabled={!catalogos} onClick={() => setAccion('editar')}>Editar</Button>
          <Button icon={<TeamOutlined />} onClick={() => setAccion('roles')}>Roles</Button>
          {ficha.activo && <Button danger icon={<StopOutlined />} onClick={() => setAccion('baja')}>Dar de baja</Button>}
        </Space>
      )}
    >
      {cargando && <Spin />}
      {error && (
        <Result status="warning" title={error} extra={<Button icon={<ReloadOutlined />} onClick={cargar}>Reintentar</Button>} />
      )}

      {ficha && (
        <FichaPestanas gestionar={gestionar} ficha={ficha} datos={<DatosFicha ficha={ficha} catalogos={catalogos} delegacion={delegacion} zona={zona} />} />
      )}

      {gestionar && ficha && (
        <>
          <EmpleadoFormModal
            abierto={accion === 'editar'}
            catalogos={catalogos}
            empleado={ficha}
            onCerrar={() => setAccion(null)}
            onCambiado={trasCambio}
            onAbrirFicha={() => undefined}
          />
          <RolesModal
            abierto={accion === 'roles'}
            idEmpleado={ficha.idEmpleado}
            rolesActuales={ficha.roles}
            catalogos={catalogos}
            onCerrar={() => setAccion(null)}
            onGuardado={trasCambio}
          />
          <BajaModal
            abierto={accion === 'baja'}
            idEmpleado={ficha.idEmpleado}
            nombre={ficha.nombreCompleto}
            onCerrar={() => setAccion(null)}
            onGuardado={trasCambio}
          />
        </>
      )}
    </Drawer>
  );
};
